import React, {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import {connect} from 'react-redux';
import Select from 'react-select';
import * as Actions from '../../store/actions';
import useDragAndDrop from '../../hooks/useDragAndDrop';
import {uploadCsv} from '../../assets/images/images';
import {
  Modal,
  DropdownIndicator,
  customStyles,
  PrimaryButton,
  Checkbox,
} from '../../components';
import {handleFileInput} from '../../helpers';
import {useMutation} from 'react-query';
import {performStatement} from '../../requests/queries/creditChecks';
import {useParams} from 'react-router-dom';

function UploadPdfModal({
  open,
  setOpen,
  apps: {allApps},
  getBankCodes,
  lookup: {banks, loading},
  setData = () => {},
}) {
  const {dragOver, setDragOver, onDragOver, onDragLeave} = useDragAndDrop();
  const [file, setFileData] = useState(null);

  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [pdfPassword, setPdfPassword] = useState('');
  const [isPassword, setIsPassword] = useState(false);

  const {slug} = useParams();

  const allBanks =
    banks &&
    banks?.map(bank => ({
      value: bank.code,
      label: bank.name,
      name: bank.name,
    }));
  useEffect(() => {
    if (selectedApp?.value) {
      getBankCodes();
    }
  }, [selectedApp?.value, getBankCodes]);
  const userApps =
    allApps &&
    allApps?.apps?.map(app => ({
      value: app._id,
      label: app.name,
      name: app.name,
    }));
  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e?.dataTransfer?.files[0];
    if (selectedFile.type.split('/')[0] !== 'application') {
      return toast.error('Please provide a valid file type (.xlsx, .csv)');
    }
    handleFileInput(selectedFile, setFileData);
  };

  const statementData = {
    app_id: selectedApp?.value,
    statement: file?.file?.split(',')[1],
    bank_code: selectedBank?.value || '894',
    customer_id: slug,
    ...(pdfPassword && {password: pdfPassword}),
  };
  const {mutate, isLoading} = useMutation(
    'credit-statement',
    obj => performStatement(obj),
    {
      onSuccess: data => {
        setData(data);
        setOpen(false);
      },
    },
  );

  return (
    <Modal
      show={open}
      onClose={setOpen}
      extraModalClasses="max-w-[528px] overflow-y-auto h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg absolute bottom-0 sm:relative lookup-modal"
      modalPosition="justify-center sm:justify-end"
      modalTitle="Upload PDF"
    >
      <form onSubmit={e => e.preventDefault()}>
        <label htmlFor="app" className="relative mt-6 mb-2">
          Select app
        </label>
        <Select
          defaultValue={selectedApp}
          value={selectedApp}
          onChange={setSelectedApp}
          options={userApps}
          placeholder="Select app"
          components={{DropdownIndicator}}
          classNamePrefix="react-select"
          className="text-start"
          styles={customStyles}
          menuPlacement="auto"
        />
        <div className="mt-8 sm:bg-white80 sm:p-6 rounded">
          <>
            <label htmlFor="bank" className="relative mb-2">
              Select bank
            </label>
            <Select
              defaultValue={selectedBank}
              value={selectedBank}
              onChange={setSelectedBank}
              options={allBanks}
              placeholder={
                loading
                  ? 'Getting all banks, please wait...'
                  : !selectedApp?.value
                  ? 'Select an app first'
                  : !allBanks
                  ? 'Error getting banks'
                  : 'Select bank'
              }
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              className={`text-start ${
                !loading &&
                selectedApp?.value &&
                !allBanks &&
                'border-[0.5px] border-danger rounded cursor-not-allowed'
              }`}
              styles={customStyles}
              menuPlacement="auto"
              id="bank"
              isDisabled={loading || !allBanks}
            />
          </>
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            style={{
              border: `${dragOver ? '3px dashed #33333322' : ''}`,
            }}
            className="mt-6 py-[22px] flex flex-col items-center gap-[2px] text-center border border-brandBlue border-dashed rounded"
          >
            <div className="flex-col items-center hidden sm:flex">
              {!file && <img src={uploadCsv} alt="" width={48} height={48} />}
              <p className="text-body">
                {dragOver ? (
                  'Please drop file here'
                ) : (
                  <span style={{color: file ? '#00000032' : ''}}>
                    Drag and drop PDF file here
                  </span>
                )}
              </p>
            </div>
            {file ? (
              <div className="text-body text-sm flex my-2 gap-[3px] items-baseline">
                <input
                  type="file"
                  id="file"
                  onChange={e => {
                    handleFileInput(e.target.files[0], setFileData);
                  }}
                  accept="application/pdf"
                />
                <label htmlFor="file">Change file</label>
              </div>
            ) : (
              <div className="text-body text-sm flex gap-[3px] items-baseline">
                <span className="hidden sm:block">or</span>
                <input
                  type="file"
                  id="file"
                  onChange={e => {
                    handleFileInput(e.target.files[0], setFileData);
                  }}
                  accept="application/pdf"
                />
                <label htmlFor="file">click here to select</label>
                from your device
              </div>
            )}
            <p className="text-body text-sm flex gap-[3px] items-baseline">
              {file ? (
                <>
                  File:{' '}
                  <span className="font-bold text-black ">{file.filename}</span>
                </>
              ) : (
                ''
              )}
            </p>
          </div>
          <div className="mt-3 flex items-center mb-3">
            <Checkbox
              label="Is there password for this PDF?"
              className="py-[10px]"
              labelStyle="text-sm text-body leading-[18px]"
              noLeftPadding
              isChecked={isPassword}
              onChange={e => {
                setIsPassword(e.target.checked);
                setPdfPassword('');
              }}
            />
            <span className="text-grey40 text-sm pl-1"> (Optional)</span>
          </div>
          {isPassword && (
            <>
              <label htmlFor="password " className="mb-2">
                Enter password
              </label>
              <input
                type="password"
                value={pdfPassword}
                onChange={e => setPdfPassword(e.target.value)}
              />
            </>
          )}
        </div>
        <PrimaryButton
          type="submit"
          className="mt-6 w-full"
          disabled={!file || !selectedApp}
          buttonText="Upload PDF"
          loading={isLoading}
          onClick={() => mutate(statementData)}
        />
      </form>
    </Modal>
  );
}
export default connect(state => state, Actions)(UploadPdfModal);
